import { PropsWithChildren, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getAuthProvider } from '../network/identityProvider/getAuthProvider';
import { Provider } from '../network/identityProvider/types';
import { createIdentitySessionInitializer, ensureIdentitySession } from '../utils/ensure-identity-session';

export const IdentityContext = createContext(
  {} as {
    sessionState: SessionState;
    getSession: (forceUpdate?: boolean) => Promise<void>;
    getAuthProvider: () => Provider;
  },
);

export enum SessionState {
  PENDING = 'pending',
  DONE = 'done',
  IDLE = 'idle',
}

export const IdentityProvider = ({ children }: PropsWithChildren) => {
  const [sessionState, setSessionState] = useState(SessionState.IDLE);
  const initializeSession = useRef(createIdentitySessionInitializer());

  useEffect(() => {
    setSessionState(SessionState.PENDING);
    initializeSession
      .current()
      .catch((error) => console.error('Failed to initialize identity session', error))
      .finally(() => setSessionState(SessionState.DONE));
  }, []);

  const get = useCallback(async (forceUpdate = false) => {
    setSessionState(SessionState.PENDING);
    try {
      await initializeSession.current();
      await ensureIdentitySession(forceUpdate);
    } finally {
      setSessionState(SessionState.DONE);
    }
  }, []);

  return (
    <IdentityContext.Provider
      value={useMemo(() => ({ sessionState, getSession: get, getAuthProvider }), [get, sessionState])}
    >
      {children}
    </IdentityContext.Provider>
  );
};
